import { classNames } from '@progress/kendo-react-common'
import {
    CardCvcElement,
    CardExpiryElement,
    CardNumberElement,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js'
import { StripeCardNumberElement } from '@stripe/stripe-js'
import { GlobalDialogType } from 'components/Modals/GlobalDialog'
import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import { useAuth } from 'context/AuthContext'
import { useTenantContext } from 'context/TenantContext'
import { useState } from 'react'
import { postApiRequest } from 'utils/fetch-helper'
import { getTranslation } from 'utils/language-helper'

export type CardSetupFormProps = {
    closeForm(card_info?: string): void
}

export function CardSetupForm({ closeForm }: CardSetupFormProps) {
    const elements = useElements()
    const stripe = useStripe()
    const { setGlobalError } = useAppErrorApiContext()
    const {
        translations,
        setGlobalLoading,
    } = useAppContext()
    const { CreateClientModal, GlobalDialog } = useAuth()
    const { tenant } = useTenantContext()
    const [card_number, setCardNumber] = useState<StripeCardNumberElement>()
    const client_id = CreateClientModal.attributes?.id as string

    const CARD_ELEMENT_OPTIONS = {
        classes: {
            base: 'w-full rounded-lg bg-white border border-gray-300 placeholder-gray-300 mt-1 py-4 px-4',
        },
    }

    async function handleUpdateCard() {
        if (!stripe || !elements || !card_number) return
        try {
            setGlobalLoading(true)

            const payment_method_result = await stripe.createPaymentMethod({
                type: 'card',
                card: card_number,
            })

            const setup_intent = await postApiRequest(
                `/v1/payments/setup-intent`,
                {
                    clientId: client_id,
                    locationId: null,
                    tenantId: tenant?.id,
                    paymentMethodId:
                        payment_method_result.paymentMethod?.id,
                }
            )

            if (setup_intent.message) {
                throw setup_intent.message
            }

            const card_info = getTranslation('card_ending', translations, {
                last4: payment_method_result.paymentMethod?.card
                    ?.last4 as string,
            })
            GlobalDialog.setAttributes({
                title: 'Successfully updated client payment card',
                message: card_info,
                className: 'w-auto',
                type: GlobalDialogType.SUCCESS,
            })
            GlobalDialog.open()
            closeForm(card_info)
        } catch (err) {
            setGlobalError(err)
        } finally {
            setGlobalLoading(false)
        }
    }

    return (
        <div>
            <div className="flex flex-col gap-5 bg-gray-100 p-5 rounded-2xl">
                <div className="flex justify-between gap-3">
                    <div className="w-2/6">
                        <label
                            htmlFor="card-holder-name"
                            className={classNames('text-gray-400')}
                        >
                            <Translation content_key="card_name" />
                        </label>
                        <input
                            type="text"
                            id="card-holder-name"
                            placeholder={getTranslation(
                                'enter_card_holder_fullname',
                                translations
                            )}
                            className={classNames(
                                'w-full mt-1 py-3 focus:ring-primary-light focus:border-primary-light block shadow-sm border-gray-300 rounded-md'
                            )}
                        />
                    </div>
                    <div className="w-2/6">
                        <label
                            htmlFor="card-number"
                            className={classNames('text-gray-400')}
                        >
                            <Translation content_key="card_number" />
                        </label>
                        <CardNumberElement
                            id="card-number"
                            options={{
                                ...CARD_ELEMENT_OPTIONS,
                                placeholder: '0000 0000 0000 0000',
                            }}
                            onChange={() => {
                                if (elements) {
                                    const element =
                                        elements.getElement(
                                            CardNumberElement
                                        )
                                    if (element) {
                                        setCardNumber(element)
                                    }
                                }
                            }}
                        />
                    </div>
                    <div className="w-1/6">
                        <label
                            htmlFor="card-expiry"
                            className={classNames('text-gray-400')}
                        >
                            <Translation content_key="expiry_date" />
                        </label>
                        <CardExpiryElement
                            id="card-expiry"
                            options={CARD_ELEMENT_OPTIONS}
                        />
                    </div>
                    <div className="w-1/6">
                        <label
                            htmlFor="card-cvc"
                            className={classNames('text-gray-400')}
                        >
                            <Translation content_key="cvc_cvv" />
                        </label>
                        <CardCvcElement
                            id="card-cvc"
                            options={CARD_ELEMENT_OPTIONS}
                        />
                    </div>
                </div>

                <div className="flex gap-3 mt-3">
                    <div className="grow self-center">
                        <Translation content_key="update_card_confirmation" />
                    </div>
                    <button
                        type="button"
                        className="border-gray-200 rounded-lg border px-10"
                        onClick={() => closeForm()}
                    >
                        <Translation content_key="cancel" />
                    </button>
                    <button
                        type="button"
                        className={classNames(
                            'group relative flex justify-center',
                            'py-3 px-12 border border-transparent',
                            'rounded-md text-white',
                            'focus:outline-none',
                            'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                        )}
                        onClick={() => handleUpdateCard()}
                    >
                        <Translation content_key="update_card" />
                    </button>
                </div>
            </div>
        </div>
    )
}

export default CardSetupForm
